import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    Divider,
    Button,
    Stack,
    IconButton,
    Chip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

/* ── Palette — matches Contact page exactly ── */
const FOREST = "#14302A";
const FOREST2 = "#1A3C34";
const GOLD = "#D4A017";
const GOLD2 = "#f0c84a";
const BG = "#f7f7f5";
const BORDER = "#ebebeb";
const GOLD_BG = "rgba(212,160,23,0.10)";
const GOLD_BDR = "rgba(212,160,23,0.30)";

const Checkout = () => {
    const [cart, setCart] = useState([]);

    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        setCart(JSON.parse(localStorage.getItem("cart")) || []);
    }, []);
    useEffect(() => {

        document.title =
            "Checkout | Tecniqa";

    }, []);

    const updateCart = (updatedCart) => {
        setCart(updatedCart);
        localStorage.setItem("cart", JSON.stringify(updatedCart));
    };

    const increaseQty = (index) => {
        const updatedCart = [...cart];
        updatedCart[index].qty += 1;
        updateCart(updatedCart);
    };

    const decreaseQty = (index) => {
        const updatedCart = [...cart];

        if (updatedCart[index].qty > 1) {
            updatedCart[index].qty -= 1;
            updateCart(updatedCart);
        }
    };

    const removeItem = (index) => {
        const updatedCart = cart.filter((_, i) => i !== index);
        updateCart(updatedCart);
    };

    const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
    const deliveryFee = subtotal > 1000 ? 0 : 49;
    const total = subtotal + deliveryFee;

    /* ── PLACE ORDER FLOW ── */
    const handlePlaceOrder = () => {
        if (user) {
            navigate("/payout");
        } else {
            localStorage.setItem("proceed_to_payout", "true"); // set flag before redirecting
            navigate("/login", {
                state: {
                    from: { pathname: "/payout" },
                },
            });
        }
    };

    /* ── Main Checkout ── */
    return (
        <Box sx={{ backgroundColor: BG, minHeight: "100vh" }}>

            {/* ── HERO HEADER ── */}
            <Box
                sx={{
                    backgroundColor: FOREST,
                    pt: 10,
                    pb: 5,
                    px: { xs: 2, sm: 4 },
                    position: "relative",
                    overflow: "hidden",

                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD2} 50%, ${GOLD} 100%)`,
                    },

                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: -60,
                        right: -60,
                        width: 220,
                        height: 220,
                        borderRadius: "50%",
                        border: `40px solid ${GOLD_BG}`,
                        pointerEvents: "none",
                    },
                }}
            >
                <Box
                    sx={{
                        maxWidth: 1050,
                        mx: "auto",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography
                            component="span"
                            sx={{
                                display: "block",
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.2em",
                                textTransform: "uppercase",
                                color: GOLD,
                                mb: 0.5,
                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                            }}
                        >
                            Tecniqa
                        </Typography>

                        <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{
                                color: "#fff",
                                letterSpacing: "-0.02em",
                                lineHeight: 1.1,
                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                            }}
                        >
                            Checkout
                        </Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/")}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "8px",
                            color: GOLD,
                            borderColor: GOLD_BDR,
                            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",

                            "&:hover": {
                                borderColor: GOLD,
                                backgroundColor: GOLD_BG,
                            },
                        }}
                    >
                        Back to Home
                    </Button>
                </Box>
            </Box>

            {/* ── BODY ── */}
            <Box
                sx={{
                    maxWidth: 1050,
                    mx: "auto",
                    px: { xs: 2, sm: 4 },
                    py: 4,
                }}
            >

                {/* FREE DELIVERY NUDGE */}
                {subtotal > 0 && subtotal <= 1000 && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            border: `1px dashed ${GOLD}`,
                            borderRadius: "8px",
                            px: 3,
                            py: 1.5,
                            mb: 3,
                            backgroundColor: GOLD_BG,
                        }}
                    >
                        <LocalShippingOutlinedIcon sx={{ color: GOLD, fontSize: 20 }} />

                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                                color: FOREST,
                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                            }}
                        >
                            Add ₹ {(1000 - subtotal).toLocaleString("en-IN")} more to unlock FREE delivery!
                        </Typography>
                    </Box>
                )}

                {subtotal > 1000 && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            border: `1px solid ${GOLD_BDR}`,
                            borderRadius: "8px",
                            px: 3,
                            py: 1.5,
                            mb: 3,
                            backgroundColor: GOLD_BG,
                        }}
                    >
                        <LocalShippingOutlinedIcon sx={{ color: GOLD, fontSize: 20 }} />

                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                                color: FOREST,
                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                            }}
                        >
                            🎉 You've unlocked FREE delivery on this order!
                        </Typography>
                    </Box>
                )}

                {/* ── TWO-COLUMN LAYOUT ── */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "1.2fr 0.8fr",
                        },
                        gap: 3,
                        alignItems: "start",
                    }}
                >

                    {/* ══ LEFT: CART ITEMS ══ */}
                    <Card
                        sx={{
                            p: { xs: 2.5, sm: 4 },
                            borderRadius: "16px",
                            border: `1px solid ${BORDER}`,
                            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                        }}
                    >
                        {/* Card heading */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "10px",
                                    backgroundColor: FOREST,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <ShoppingCartOutlinedIcon
                                    sx={{
                                        color: GOLD,
                                        fontSize: 20,
                                    }}
                                />
                            </Box>

                            <Typography
                                variant="h6"
                                fontWeight={800}
                                sx={{
                                    color: FOREST,
                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                Order Summary
                            </Typography>

                            {cart.length > 0 && (
                                <Chip
                                    label={`${cart.length} item${cart.length > 1 ? "s" : ""}`}
                                    size="small"
                                    sx={{
                                        ml: "auto",
                                        backgroundColor: GOLD_BG,
                                        color: FOREST,
                                        fontWeight: 700,
                                        border: `1px solid ${GOLD_BDR}`,
                                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                    }}
                                />
                            )}
                        </Box>

                        {/* Empty state */}
                        {cart.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: 8,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "#f0f0ee",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <ShoppingCartOutlinedIcon
                                        sx={{
                                            fontSize: 36,
                                            color: "#bbb",
                                        }}
                                    />
                                </Box>

                                <Typography
                                    fontWeight={600}
                                    color="text.secondary"
                                    sx={{
                                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                    }}
                                >
                                    Your cart is empty.
                                </Typography>

                                <Button
                                    variant="contained"
                                    onClick={() => navigate("/")}
                                    sx={{
                                        backgroundColor: FOREST,
                                        color: GOLD,
                                        textTransform: "none",
                                        fontWeight: 700,
                                        borderRadius: "8px",
                                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",

                                        "&:hover": {
                                            backgroundColor: FOREST2,
                                        },
                                    }}
                                >
                                    Continue Shopping
                                </Button>
                            </Box>
                        ) : (
                            <Stack
                                spacing={0}
                                divider={<Divider sx={{ borderColor: BORDER }} />}
                            >
                                {cart.map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: { xs: 1.5, sm: 2.5 },
                                            py: 2.5,
                                            flexWrap: {
                                                xs: "wrap",
                                                sm: "nowrap",
                                            },
                                        }}
                                    >

                                        {/* PRODUCT IMAGE */}
                                        <Box
                                            sx={{
                                                width: { xs: 68, sm: 82 },
                                                height: { xs: 68, sm: 82 },
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                                border: `1px solid ${BORDER}`,
                                                backgroundColor: "#fafaf8",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {item.image ? (
                                                <img
                                                    src={item.image}
                                                    alt={item.title}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "contain",
                                                        padding: "8px",
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        backgroundColor: GOLD_BG,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <InventoryOutlinedIcon
                                                        sx={{
                                                            color: GOLD,
                                                            fontSize: 28,
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>

                                        {/* PRODUCT INFO */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                fontWeight={700}
                                                sx={{
                                                    color: FOREST,
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                    fontSize: {
                                                        xs: "0.875rem",
                                                        sm: "0.95rem",
                                                    },
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.title}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#888",
                                                    mt: 0.4,
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                ₹ {item.price.toLocaleString("en-IN")} / unit
                                            </Typography>

                                            {item.brand && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: GOLD,
                                                        fontWeight: 700,
                                                        letterSpacing: "0.08em",
                                                        textTransform: "uppercase",
                                                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                    }}
                                                >
                                                    {item.brand}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* QTY CONTROLS */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                border: `1.5px solid ${BORDER}`,
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => decreaseQty(index)}
                                                disabled={item.qty === 1}
                                                sx={{
                                                    borderRadius: 0,
                                                    color: FOREST,

                                                    "&:hover": {
                                                        backgroundColor: GOLD_BG,
                                                    },

                                                    "&.Mui-disabled": {
                                                        color: "#ccc",
                                                    },
                                                }}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>

                                            <Typography
                                                sx={{
                                                    minWidth: 32,
                                                    textAlign: "center",
                                                    fontWeight: 700,
                                                    fontSize: "0.9rem",
                                                    color: FOREST,
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                {item.qty}
                                            </Typography>

                                            <IconButton
                                                size="small"
                                                onClick={() => increaseQty(index)}
                                                sx={{
                                                    borderRadius: 0,
                                                    color: FOREST,

                                                    "&:hover": {
                                                        backgroundColor: GOLD_BG,
                                                    },
                                                }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        {/* ITEM TOTAL */}
                                        <Typography
                                            fontWeight={800}
                                            sx={{
                                                minWidth: {
                                                    xs: "auto",
                                                    sm: 96,
                                                },
                                                textAlign: "right",
                                                color: FOREST,
                                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                fontSize: "0.95rem",
                                                flexShrink: 0,
                                            }}
                                        >
                                            ₹ {(item.price * item.qty).toLocaleString("en-IN")}
                                        </Typography>

                                        {/* REMOVE BUTTON */}
                                        <IconButton
                                            onClick={() => removeItem(index)}
                                            sx={{
                                                color: "#ccc",
                                                flexShrink: 0,

                                                "&:hover": {
                                                    color: "#e53935",
                                                    backgroundColor: "rgba(229,57,53,0.06)",
                                                },
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Card>

                    {/* ══ RIGHT: PRICE SUMMARY ══ */}
                    {cart.length > 0 && (
                        <Box
                            sx={{
                                position: { md: "sticky" },
                                top: { md: 24 },
                            }}
                        >
                            <Card
                                sx={{
                                    borderRadius: "16px",
                                    border: `1px solid ${BORDER}`,
                                    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Gold accent top bar */}
                                <Box
                                    sx={{
                                        height: 3,
                                        background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD2} 50%, ${GOLD} 100%)`,
                                    }}
                                />

                                <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                                    <Typography
                                        fontWeight={800}
                                        sx={{
                                            color: FOREST,
                                            mb: 2.5,
                                            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                            letterSpacing: "-0.01em",
                                        }}
                                    >
                                        Price Breakdown
                                    </Typography>

                                    <Stack spacing={1.5}>
                                        {/* Subtotal */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#888",
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                Subtotal ({cart.reduce((s, i) => s + i.qty, 0)} items)
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{
                                                    color: FOREST,
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                ₹ {subtotal.toLocaleString("en-IN")}
                                            </Typography>
                                        </Box>

                                        {/* Delivery */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#888",
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                Delivery
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{
                                                    color: deliveryFee === 0 ? "#1a8a4a" : FOREST,
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                {deliveryFee === 0 ? "FREE" : `₹ ${deliveryFee}`}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ borderColor: BORDER }} />

                                        {/* Total */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography
                                                fontWeight={800}
                                                sx={{
                                                    color: FOREST,
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                Total
                                            </Typography>

                                            <Typography
                                                fontWeight={800}
                                                sx={{
                                                    color: FOREST,
                                                    fontSize: "1.2rem",
                                                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                }}
                                            >
                                                ₹ {total.toLocaleString("en-IN")}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    {/* PLACE ORDER BUTTON */}
                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        onClick={handlePlaceOrder}
                                        sx={{
                                            mt: 3,
                                            py: 1.6,
                                            fontWeight: 700,
                                            borderRadius: "8px",
                                            textTransform: "none",
                                            fontSize: "0.9rem",
                                            letterSpacing: "0.08em",
                                            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                            backgroundColor: FOREST,
                                            color: GOLD,
                                            boxShadow: "none",
                                            transition: "all 0.25s ease",

                                            "&:hover": {
                                                backgroundColor: FOREST2,
                                                boxShadow: "0 8px 24px rgba(20,48,42,0.25)",
                                                transform: "translateY(-1px)",
                                            },

                                            "&:active": {
                                                transform: "translateY(0)",
                                            },
                                        }}
                                    >
                                        Proceed to Pay
                                    </Button>

                                    {/* Trust line */}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: "block",
                                            textAlign: "center",
                                            mt: 2,
                                            color: "#aaa",
                                            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                            letterSpacing: "0.02em",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        ✓ Secure checkout &nbsp;·&nbsp; Genuine warranty
                                        <br />
                                        ✓ Pan-India delivery
                                    </Typography>
                                </Box>
                            </Card>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Checkout;